import { Link, useRouter } from '@tanstack/react-router';

import { Button, ErrorState } from '@keboola/design';

/**
 * What an uncaught render error renders.
 *
 * Without an `errorComponent` React unmounts the whole tree, so a single bad value anywhere leaves a
 * white document with the app gone and nothing to click. `router.invalidate()` retries the failed
 * route rather than reloading the tab, so the session and the query cache survive the attempt.
 */
export function RouteErrorPage({ error }: { error: unknown }) {
  const router = useRouter();
  // `unknown`, not `Error`: a boundary is handed whatever was thrown, and `throw 'oops'` is legal.
  // Narrowing rather than asserting keeps the description honest — a non-`Error` has no `message`,
  // and rendering `undefined` under a heading would leave a stray empty paragraph.
  const detail = error instanceof Error ? error.message : null;

  return (
    <div className="tw:flex tw:min-h-svh tw:items-center tw:justify-center tw:p-6">
      <ErrorState>
        <ErrorState.Title as="h1">Something went wrong</ErrorState.Title>
        {detail !== null && <ErrorState.Description>{detail}</ErrorState.Description>}
        <ErrorState.Actions>
          <Button variant="secondary" onClick={() => void router.invalidate()}>
            Try again
          </Button>
          <Button asChild variant="outline">
            <Link to="/components">Browse components</Link>
          </Button>
        </ErrorState.Actions>
      </ErrorState>
    </div>
  );
}
